import { memo, useCallback, useEffect, useRef, useState } from "react";

import EmojiSymbolsIcon from "@mui/icons-material/EmojiSymbols";
import {
  Box,
  IconButton,
  SxProps,
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
} from "@mui/material";

import { Emoticon } from "@/extensions/obituary/types";

import EmotionPicker from "./EmoticonPicker";

import { Nullish } from "@/utils/types";

const styles: Record<string, SxProps> = {
  textField: {
    width: "100%",
    bgcolor: "background.paper",

    "& .MuiFilledInput-root": {
      bgcolor: "background.paper",
      border: "1px solid",
      borderColor: "grey.400",
    },
  },

  emoticonButton: {
    position: "absolute",
    right: 6,
    bottom: 6,
    bgcolor: "background.paper",
    borderRadius: "50%",
    border: "1px solid",
    borderColor: "grey.400",
    transform: "scale(0.7)",
    zIndex: 1,

    "&:hover": { borderColor: "black" },
  },
};

type CursorPosition = Nullish<number>;

export type TextFieldProps = Omit<MuiTextFieldProps, "onChange" | "value"> & {
  debounce?: number;
  onChange: (value: string) => void;
  emoticons?: Emoticon[];
  emoticonsButtonSx?: SxProps;
  closeEmoticonPickerOnSelect?: boolean;
  onEmoticonSelect?: (emoticon: Emoticon) => void;
  value: Nullish<string>;
};

const TextField: React.FC<TextFieldProps> = ({
  closeEmoticonPickerOnSelect,
  debounce,
  disabled,
  onChange,
  onEmoticonSelect,
  value: initialValue,
  sx,
  emoticons,
  emoticonsButtonSx,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(initialValue ?? "");
  const anchorRef = useRef<HTMLButtonElement>(null);

  const textFieldRef = useRef<HTMLTextAreaElement>(null);
  const [cursorPosition, setCursorPosition] = useState<CursorPosition>(null);

  // Store onChange in a ref so we always have the latest version
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Keep track of pending debounce timer
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingValueRef = useRef<string | null>(null);

  const hasEmoticons = Array.isArray(emoticons) && emoticons.length > 0;

  // Sync internal state when external value prop changes, but only if we don't have pending changes
  useEffect(() => {
    if (pendingValueRef.current === null) {
      setValue(initialValue ?? "");
    }
  }, [initialValue]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const [refocusTextFieldAt, setRefocusTextFieldAt] =
    useState<CursorPosition>(null);

  // Get the cursor position for the emoticon picker.
  useEffect(() => {
    if (!hasEmoticons) return;

    const textField = textFieldRef.current;
    if (textField == null) return;

    const handleSelectionChange = () => {
      setCursorPosition(textField.selectionStart);
    };

    textField.addEventListener("select", handleSelectionChange);
    textField.addEventListener("click", handleSelectionChange);
    textField.addEventListener("keyup", handleSelectionChange);

    return () => {
      textField.removeEventListener("select", handleSelectionChange);
      textField.removeEventListener("click", handleSelectionChange);
      textField.removeEventListener("keyup", handleSelectionChange);
    };
  }, []);

  const handleOnChange = useCallback(
    ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
      setValue(value);

      // Clear any existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (debounce != null) {
        // Store the pending value
        pendingValueRef.current = value;

        // Set up new debounce timer
        debounceTimerRef.current = setTimeout(() => {
          onChangeRef.current?.(value);
          pendingValueRef.current = null;
          debounceTimerRef.current = null;
        }, debounce);
      } else {
        // No debounce, call immediately
        onChangeRef.current?.(value);
      }
    },
    [debounce],
  );

  const handleBlur = useCallback(() => {
    // Flush any pending debounced changes immediately on blur
    if (pendingValueRef.current !== null) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      onChangeRef.current?.(pendingValueRef.current);
      pendingValueRef.current = null;
    }
  }, []);

  const handleSetOpen = useCallback(() => setOpen((prev) => !prev), []);

  const handleEmoticonClick = useCallback(
    (emoticon: Emoticon) => {
      if (onEmoticonSelect) {
        if (closeEmoticonPickerOnSelect) setOpen(false);
        onEmoticonSelect(emoticon);
        return;
      }

      setValue((value) => {
        if (closeEmoticonPickerOnSelect) setOpen(false);

        const text =
          cursorPosition != null
            ? `${value?.slice(0, cursorPosition) ?? ""} ${emoticon.code} ${value?.slice(cursorPosition) ?? ""}`
            : `${value} ${emoticon.code}`;

        onChangeRef.current?.(text);

        // Refocusing has to happen _after_ next render.
        if (textFieldRef.current != null && cursorPosition != null) {
          setRefocusTextFieldAt(cursorPosition + emoticon.code.length + 2);
        }

        return text;
      });
    },
    [closeEmoticonPickerOnSelect, cursorPosition, onEmoticonSelect, setValue],
  );

  // Refocus text field after inserting a emoticon.
  // Has to take place the render after insertion.
  useEffect(() => {
    if (refocusTextFieldAt == null) return;

    const textField = textFieldRef.current;
    if (textField == null) return;

    textField.focus();
    textField.setSelectionRange(refocusTextFieldAt, refocusTextFieldAt);
    setRefocusTextFieldAt(null);
  }, [refocusTextFieldAt]);

  return (
    <Box sx={{ position: "relative" }}>
      <MuiTextField
        {...props}
        disabled={disabled}
        inputRef={textFieldRef}
        // @ts-expect-error - Incorrect sx type
        sx={{
          ...styles.textField,
          ...sx,
          cursor: disabled ? "not-allowed" : "text",
        }}
        value={value}
        onBlur={handleBlur}
        onChange={handleOnChange}
      />

      {hasEmoticons && (
        <>
          <IconButton
            ref={anchorRef}
            disableRipple
            sx={
              {
                ...styles.emoticonButton,
                ...(emoticonsButtonSx ?? {}),
              } as SxProps
            }
            tabIndex={-1}
            onClick={handleSetOpen}
          >
            <EmojiSymbolsIcon />
          </IconButton>

          <EmotionPicker
            anchor={anchorRef}
            emoticons={emoticons}
            open={open}
            onClickAway={handleSetOpen}
            onEmoticonClick={handleEmoticonClick}
          />
        </>
      )}
    </Box>
  );
};

export default memo(TextField);
